/** Geometria ZONELOR DE REGIM pe hartă: tăierea lotului după o linie trasă
 *  de om, și linia de plecare când actul n-a spus unde e limita.
 *
 *  Tot în grade [lat, lon], ca restul hărții, cu aceeași corecție de
 *  latitudine ca `Harta.arie`: lucrul se face într-un reper local în metri,
 *  altfel „paralel cu strada" ar ieși strâmb cu vreo 30 % în România.
 *
 *  Tăierea de aici e doar PREVIZUALIZAREA de sub mână: cea care se scrie în
 *  dosar o face serverul, în Stereo 70, din aceeași linie
 *  (`zone_urbanistice.corectie_din_linie`), ca o singură geometrie să fie
 *  adevărul. */
import { taie } from "../viewer/taietor";
import type { Latura } from "./dosarclient";

export type Pct = [number, number];
export interface Linie { p0: Pct; p1: Pct; dePartea: Pct }

const K_LAT = 111132.92;

function reper(pts: Pct[]) {
  const lat0 = (pts.reduce((s, q) => s + q[0], 0) / pts.length) * Math.PI / 180;
  const kLon = 111320 * Math.cos(lat0);
  return {
    laMetri: (p: Pct): [number, number] => [p[1] * kLon, p[0] * K_LAT],
    laGrade: (m: [number, number]): Pct => [m[1] / K_LAT, m[0] / kLon],
  };
}

/** Bucata lotului de partea punctului `dePartea` față de dreapta p0–p1.
 *  `null` când dreapta nu taie lotul (sau punctul nu cade în nicio bucată). */
export function taiePeLinie(contur: Pct[], l: Linie): Pct[] | null {
  if (contur.length < 3) return null;
  const { laMetri, laGrade } = reper(contur);
  const lot = contur.map(laMetri);
  const a = laMetri(l.p0), b = laMetri(l.p1), r = laMetri(l.dePartea);
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const lung = Math.hypot(dx, dy);
  if (lung < 1e-6) return null;
  const ux = dx / lung, uy = dy / lung;
  // Normala spre partea cerută.
  let nx = -uy, ny = ux;
  if ((r[0] - a[0]) * nx + (r[1] - a[1]) * ny < 0) { nx = -nx; ny = -ny; }
  const mult = 1e5;
  const semiplan: [number, number][] = [
    [a[0] - ux * mult, a[1] - uy * mult],
    [b[0] + ux * mult, b[1] + uy * mult],
    [b[0] + ux * mult + nx * mult, b[1] + uy * mult + ny * mult],
    [a[0] - ux * mult + nx * mult, a[1] - uy * mult + ny * mult],
  ];
  const taiat = taie().intersection([lot], [semiplan]);
  if (!taiat.length) return null;
  // Cea mai mare bucată: un lot concav se poate rupe în două de aceeași parte;
  // previzualizarea arată una, serverul le reunește pe amândouă.
  let bun: [number, number][] | null = null, arieMax = 0;
  for (const poly of taiat) {
    const inel = poly[0];
    let s = 0;
    for (let i = 0; i < inel.length - 1; i++) {
      s += inel[i][0] * inel[i + 1][1] - inel[i + 1][0] * inel[i][1];
    }
    if (Math.abs(s) / 2 > arieMax) { arieMax = Math.abs(s) / 2; bun = inel; }
  }
  if (!bun || arieMax < 1) return null;
  // Tot lotul de aceeași parte = dreapta nu-l taie: nu e o limită.
  let arieLot = 0;
  for (let i = 0; i < lot.length; i++) {
    const p = lot[i], q = lot[(i + 1) % lot.length];
    arieLot += p[0] * q[1] - q[0] * p[1];
  }
  if (arieMax >= Math.abs(arieLot) / 2 * (1 - 1e-3)) return null;
  const pct = bun.slice(0, -1).map((m) => laGrade([m[0], m[1]]));
  return pct.length >= 3 ? pct : null;
}

/** Aria unei bucăți, în m², pe aceeași corecție de latitudine. */
export function ariaBucatii(pct: Pct[]): number {
  if (pct.length < 3) return 0;
  const { laMetri } = reper(pct);
  const m = pct.map(laMetri);
  let s = 0;
  for (let i = 0; i < m.length; i++) {
    const a = m[i], b = m[(i + 1) % m.length];
    s += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(s) / 2;
}

/** Cu ce linie se pleacă, când nu se știe limita: paralelă cu frontul
 *  stradal (cea mai lungă latură la stradă, altfel cea mai lungă), la o
 *  treime din adâncimea lotului, cu zona de partea străzii — fiindcă
 *  zonele mixte stau de obicei la bulevard, iar locuințele în spate. Omul
 *  o trage de unde e. */
export function liniaDePlecare(contur: Pct[], laturi: Latura[]): Linie | null {
  if (contur.length < 3) return null;
  const { laMetri, laGrade } = reper(contur);
  const m = contur.map(laMetri);
  const n = m.length;
  const lung = (i: number) => Math.hypot(m[(i + 1) % n][0] - m[i][0], m[(i + 1) % n][1] - m[i][1]);
  const laStrada = [...Array(n).keys()].filter((i) => laturi[i]?.tip === "strada");
  const i0 = (laStrada.length ? laStrada : [...Array(n).keys()])
    .reduce((best, i) => (lung(i) > lung(best) ? i : best));
  const a = m[i0], b = m[(i0 + 1) % n];
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const L = Math.hypot(dx, dy);
  if (L < 1e-6) return null;
  const ux = dx / L, uy = dy / L;
  let nx = -uy, ny = ux;
  // Centrul lotului spune care parte e înăuntru.
  const cx = m.reduce((s, q) => s + q[0], 0) / n, cy = m.reduce((s, q) => s + q[1], 0) / n;
  if ((cx - a[0]) * nx + (cy - a[1]) * ny < 0) { nx = -nx; ny = -ny; }
  const adancime = Math.max(...m.map((q) => (q[0] - a[0]) * nx + (q[1] - a[1]) * ny));
  if (!(adancime > 1)) return null;
  const d = adancime / 3;
  // Dreapta la adâncimea `d`, tăiată de laturile lotului.
  const taieri: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const p = m[i], q = m[(i + 1) % n];
    const hp = (p[0] - a[0]) * nx + (p[1] - a[1]) * ny - d;
    const hq = (q[0] - a[0]) * nx + (q[1] - a[1]) * ny - d;
    if ((hp <= 0 && hq > 0) || (hp > 0 && hq <= 0)) {
      const t = hp / (hp - hq);
      taieri.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]);
    }
  }
  if (taieri.length < 2) return null;
  // Cele două tăieri cele mai depărtate una de alta.
  let p0 = taieri[0], p1 = taieri[1], dist = -1;
  for (const s of taieri) for (const e of taieri) {
    const dd = Math.hypot(e[0] - s[0], e[1] - s[1]);
    if (dd > dist) { dist = dd; p0 = s; p1 = e; }
  }
  const mij: [number, number] = [(p0[0] + p1[0]) / 2, (p0[1] + p1[1]) / 2];
  const dePartea: [number, number] = [mij[0] - nx * d / 2, mij[1] - ny * d / 2];
  return { p0: laGrade(p0), p1: laGrade(p1), dePartea: laGrade(dePartea) };
}

/** Linia de pe care se pleacă pentru o zonă care ARE deja contur: muchia ei
 *  cea mai lungă care NU stă pe limita lotului (adică limita dintre zone), cu
 *  zona de partea centrului ei. `null` când toate muchiile sunt pe lot. */
export function liniaDinContur(zona: Pct[], lot: Pct[]): Linie | null {
  if (zona.length < 3 || lot.length < 3) return null;
  const { laMetri, laGrade } = reper(lot);
  const L = lot.map(laMetri), Z = zona.map(laMetri);
  const peLot = (p: [number, number]) => {
    let dist = Infinity;
    for (let i = 0; i < L.length; i++) {
      const a = L[i], b = L[(i + 1) % L.length];
      const dx = b[0] - a[0], dy = b[1] - a[1];
      const l2 = dx * dx + dy * dy || 1;
      const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2));
      dist = Math.min(dist, Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy));
    }
    return dist < 0.5;
  };
  let bun: [[number, number], [number, number]] | null = null, lungMax = 0;
  for (let i = 0; i < Z.length; i++) {
    const a = Z[i], b = Z[(i + 1) % Z.length];
    const mij: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    if (peLot(mij)) continue;
    const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (l > lungMax) { lungMax = l; bun = [a, b]; }
  }
  if (!bun) return null;
  const cx = Z.reduce((s, q) => s + q[0], 0) / Z.length;
  const cy = Z.reduce((s, q) => s + q[1], 0) / Z.length;
  return { p0: laGrade(bun[0]), p1: laGrade(bun[1]), dePartea: laGrade([cx, cy]) };
}

/** Partea cealaltă a aceleiași linii: `dePartea` oglindit față de dreaptă. */
export function inverseaza(l: Linie): Linie {
  const { laMetri, laGrade } = reper([l.p0, l.p1, l.dePartea]);
  const a = laMetri(l.p0), b = laMetri(l.p1), r = laMetri(l.dePartea);
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy || 1;
  const t = ((r[0] - a[0]) * dx + (r[1] - a[1]) * dy) / l2;
  const px = a[0] + t * dx, py = a[1] + t * dy;
  return { ...l, dePartea: laGrade([2 * px - r[0], 2 * py - r[1]]) };
}
